// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { Size, Tag, Target } from "@quartzds/core";
import type { ComponentInterface, EventEmitter } from "@stencil/core";
import {
  Component,
  Element,
  Event,
  Fragment,
  h,
  Listen,
  Prop,
} from "@stencil/core";
import type { LiteralUnion } from "type-fest";

import type { Attributes, QdsFocusEventDetail } from "../../helpers";
import { inheritAriaAttributes, pickFocusEventAttributes } from "../../helpers";

/**
 * Do not use separately!
 * Is a part of navigational components only!
 * `<qds-web-title-link>` Ghost link is a hyperlink that is intentionally styled to be transparent or nearly invisible until it is interacted with, to reduce visual clutter while still providing access to additional content or actions.
 *
 * @see https://quartz.se.com/build/components/title-link
 */
@Component({
  tag: "qds-web-title-link",
  shadow: { delegatesFocus: true },
  styleUrl: "title-link.css",
})
export class titleLink implements ComponentInterface {
  /**
   * The name of the icon to render.
   *
   * Available names depend on the icon library being used.
   */
  @Prop() public readonly iconName?: string;

  /**
   * The name of a registered icon library.
   */
  @Prop() public readonly iconLibrary: string = "default";

  /**
   * The titleLink's size.
   */
  @Prop() public readonly size: Size = "standard";

  /**
   * The titleLink's text.
   */
  @Prop() public readonly text?: string;

  /**
   * Prevents the titleLink from being interacted with: it cannot be pressed or
   * focused.
   *
   */
  @Prop() public readonly disabled?: boolean = false;

  /**
   * When set, clicking on the title-link will redirect to the href specified.
   *
   * @webnative
   */
  @Prop() public readonly href?: string;

  /**
   * Where to display the linked URL, as the name for a browsing context (a
   * tab, window, or
   * [`<iframe>`](https://developer.mozilla.org/docs/Web/HTML/Element/iframe).
   * The following keywords have special meanings for where to load the URL:
   *
   * - `_blank`: usually a new tab, but users can configure browsers to open a new
   *   window instead.
   * - `_parent`: the parent browsing context of the current one. If no parent,
   *   behaves as `_self`.
   * - `_self`: the current browsing context.
   * - `_top`: the topmost browsing context (the "highest" context that's an
   *   ancestor of the current one). If no ancestors, behaves as `_self`.
   *
   * @webnative
   */
  @Prop() public readonly target?: LiteralUnion<Target, string>;

  /**
   * The title link's tag. Should be used if the title link is used as a heading.
   */
  @Prop() public tag?: Tag;

  /**
   * Emitted when the corresponding megamenu is closed.
   */
  @Event({ eventName: "qds-nav-item:close" })
  public closeNavTabs: EventEmitter<boolean>;

  /**
   * Emitted when the corresponding megamenu is opened.
   */
  @Event({ eventName: "qds-nav-item:open" })
  public openNavTabs: EventEmitter<boolean>;

  /**
   * Emitted when the titleLink loses focus.
   */
  @Event({ eventName: "qdsBlur", bubbles: false, cancelable: false })
  private readonly blurEmitter!: EventEmitter<QdsFocusEventDetail>;

  /**
   * Emitted when the titleLink gains focus.
   */
  @Event({ eventName: "qdsFocus", bubbles: false, cancelable: false })
  private readonly focusEmitter!: EventEmitter<QdsFocusEventDetail>;

  @Element() private readonly element!: HTMLAnchorElement;

  private inheritedAttributes: Attributes = {};

  get #computedSize(): Size | undefined {
    switch (this.size) {
      case "standard":
      case "small":
      case "large": {
        return this.size;
      }
      default: {
        return "standard";
      }
    }
  }

  @Listen("click")
  protected onClick(event: MouseEvent): void {
    if (this.disabled === true) event.stopImmediatePropagation();
  }

  public componentWillLoad(): void {
    this.inheritedAttributes = inheritAriaAttributes(this.element);
  }

  public render() {
    const TitleLink = this.href === undefined || this.href === "" ? "div" : "a";
    const TitleLinkTag = this.tag ?? Fragment;

    return (
      <TitleLinkTag class="qds-title-link-heading">
        <TitleLink
          class="qds-title-link"
          tabindex={this.disabled === true ? -1 : undefined}
          aria-disabled={this.disabled === true ? "true" : undefined}
          data-size={this.#computedSize}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          href={TitleLink === "a" ? this.href : undefined}
          target={TitleLink === "a" ? this.target : undefined}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...this.inheritedAttributes}
          onClick={() => {
            if (TitleLink === "a" && this.target !== "_blank") {
              this.closeNavTabs.emit(true);
              this.openNavTabs.emit(false);
            }
          }}
        >
          <p>{this.text}</p>
          {TitleLink === "a" && (
            <div class="qds-icon-wrapper">
              <qds-icon
                name={this.iconName ?? ""}
                library={this.iconLibrary}
                aria-hidden="true"
              />
              {this.target === "_blank" && (
                <span class="qds-sr-only">opens new tab</span>
              )}
            </div>
          )}
        </TitleLink>
      </TitleLinkTag>
    );
  }

  private readonly onFocus = (event: FocusEvent): void => {
    this.focusEmitter.emit(pickFocusEventAttributes(event));
  };

  private readonly onBlur = (event: FocusEvent): void => {
    this.blurEmitter.emit(pickFocusEventAttributes(event));
  };
}
